import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  FormGroup,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import SkeletonList from '../../../../../components/MUIComponents/SkeletonList';
import { ISport } from '../../../../../interfaces/sport.interface';
import FilterLayout from '../../../../../layouts/FilterLayout';
import { errorHelper } from '../../../../../utils/helper/error-helper';
import CheckBox from './components/CheckBox';

interface ICandidateSportFilterProps {
  sports: ISport[] | undefined;
  isLoading: boolean;
  isError: boolean;
  selectedSports: ISport[];
  label: string;
  error: FetchBaseQueryError | SerializedError | undefined;
  onShowMore: () => void;
  totalSportsCount: number;
  handleSearchChange: (data: string) => void;
  handleSelectedSport: (sport: ISport) => void;
  resetTrigger: boolean;
}

const CandidateSportFilter = ({
  sports,
  isLoading,
  isError,
  selectedSports,
  label,
  error,
  onShowMore,
  totalSportsCount,
  handleSearchChange,
  resetTrigger,
  handleSelectedSport,
}: ICandidateSportFilterProps) => {
  const [searchText, setSearchText] = useState('');
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    setSearchText('');
    setAllSelected(false);
  }, [resetTrigger]);

  const handleAllSportsChange = () => {
    if (allSelected) {
      selectedSports.forEach(sport => handleSelectedSport(sport));
    } else {
      selectedSports.forEach(sport => handleSelectedSport(sport));
      sports?.forEach(sport => handleSelectedSport(sport));
    }
    setAllSelected(!allSelected);
  };

  const handleSingleSportChange = (sport: ISport) => {
    if (allSelected) {
      selectedSports.forEach(sport => handleSelectedSport(sport));
      setAllSelected(false);
    }
    handleSelectedSport(sport);
  };

  if (isLoading) {
    return (
      <Box
        sx={{ p: '22px 16px 11px 16px' }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <SkeletonList />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography sx={{ p: '22px 16px 11px 16px' }} color="error" variant="h4">
        {errorHelper(error)}
      </Typography>
    );
  }

  return (
    <FilterLayout name={label}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.375rem',
          padding: '0 1rem 1.375rem',
        }}
      >
        <FormGroup>
          {/* Default "All" Checkbox */}
          <CheckBox
            label={'All'}
            checked={allSelected}
            onChange={handleAllSportsChange}
          />
          {/* List of Sports */}
          {sports?.map(sport => (
            <CheckBox
              key={sport.id}
              label={sport.name}
              checked={
                !!selectedSports.find(
                  selectedSport => selectedSport.id === sport.id
                )
              }
              onChange={() => handleSingleSportChange(sport)}
            />
          ))}
        </FormGroup>

        {sports && sports.length < totalSportsCount && (
          <Typography
            onClick={onShowMore}
            variant="h4"
            component="h4"
            color={'text.accent'}
            sx={{ marginTop: '10px', cursor: 'pointer' }}
          >
            + Show more
          </Typography>
        )}

        <TextField
          variant="outlined"
          placeholder="Search..."
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            handleSearchChange(e.target.value.trim());
          }}
          sx={searchFieldStyles}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ p: 0 }}>
                <SearchIcon sx={iconSizes} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </FilterLayout>
  );
};

export default CandidateSportFilter;

const searchFieldStyles = {
  width: '100%',
  maxWidth: { md: '16rem', lg: '20.125rem' },
  '& .MuiInputBase-root': { height: '3.125rem', padding: '0.75rem 1rem' },
  '& .MuiOutlinedInput-input': { height: '3.125rem !important', padding: 0 },
  '& .MuiSvgIcon-root': { backgroundColor: 'transparent' },
};

const iconSizes = { width: '1.25rem', height: '1.25rem' };
